import { theme, type as text } from "../../theme";

// The maturity self-assessment the reference standards use instead of a status (NIST CSF tiers,
// IWA 48 principle levels): the level pill for a group header, and the level picker for inside
// it. Levels climb from 1; 0 is "not assessed". Colours are meaning (how far along), from tokens.

export interface MaturityLevel { value: number; label: string; description?: string }

/** Token colour for a level on a scale of `max` levels: low reads as danger, the top as success. */
export function maturityColour(value: number, max: number): string {
  if (value <= 0) return theme.color.border;
  const ratio = value / max;
  if (ratio >= 1) return theme.color.success;
  if (ratio >= 0.7) return theme.color.accent;
  if (ratio >= 0.4) return theme.color.warning;
  return theme.color.danger;
}

export function MaturityPill({ level, levels }: { level: number; levels: MaturityLevel[] }) {
  const max = levels.length;
  const current = levels.find((l) => l.value === level);
  if (!current || level <= 0) {
    return <span style={{ ...text.meta, fontWeight: 700, padding: "2px 8px", borderRadius: 3, background: theme.color.panel, border: `1px solid ${theme.color.borderStrong}`, whiteSpace: "nowrap" }}>Not assessed</span>;
  }
  return (
    <span style={{ ...text.meta, fontWeight: 700, padding: "2px 8px", borderRadius: 3, background: maturityColour(level, max), color: "#fff", whiteSpace: "nowrap" }}>
      {current.label}
    </span>
  );
}

/** Pick a level; picking the current one again clears it (0). Read-only without `onPick`. */
export function MaturityPicker({ level, levels, onPick, label = "Self-assessed level" }: {
  level: number;
  levels: MaturityLevel[];
  onPick?: (value: number) => void;
  label?: string;
}) {
  const max = levels.length;
  return (
    <div>
      <div style={text.label}>{label}</div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {levels.map((l) => {
          const active = level === l.value;
          const reached = level >= l.value;
          return (
            <button
              key={l.value}
              type="button"
              disabled={!onPick}
              title={l.description ?? l.label}
              onClick={() => onPick?.(active ? 0 : l.value)}
              style={{
                flex: "1 1 90px", padding: "6px 8px", borderRadius: 3, cursor: onPick ? "pointer" : "default",
                fontSize: text.meta.fontSize, fontWeight: active ? 700 : 500,
                border: `1px solid ${reached ? maturityColour(l.value, max) : theme.color.borderStrong}`,
                background: reached ? maturityColour(l.value, max) : "transparent",
                color: reached ? "#fff" : theme.color.muted,
              }}
            >
              {l.value}. {l.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
